import { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import AutoAwesomeRounded from "@mui/icons-material/AutoAwesomeRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import HistoryRounded from "@mui/icons-material/HistoryRounded";

/**
 * AI Assistant side panel — a Claude chat drawer built with MUI + WHB
 * visual language.
 *
 * Backend is intentionally stubbed (no /api/claude-chat wired yet). Clicking
 * "Send" echoes the message locally and responds with a canned "not connected
 * yet" note so the UX flow is demo-able.
 *
 * When the real proxy lands, the fetch() in handleSend() should stream from
 * /api/claude-chat (SSE).
 */

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  ts: number;
}

const SEED_GREETING: Message = {
  id: "seed-greeting",
  role: "assistant",
  content:
    "I can help you think through trading strategies, explain what a bot is doing, or " +
    "summarise your recent activity. Try: \"What changed in my bots this week?\" or " +
    "\"Explain the Four Pillars strategy in plain English.\"",
  ts: Date.now()
};

const QUICK_PROMPTS = [
  "Summarise this week's bot performance",
  "Compare Four Pillars vs Golden Compass",
  "Draft a covered-call strategy for AAPL",
  "What's the Sharpe ratio of my top bot?"
];

interface Props {
  open: boolean;
  onClose: () => void;
}

export function AiAssistantPanel({ open, onClose }: Props) {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([SEED_GREETING]);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  const handleSend = async () => {
    const text = draft.trim();
    if (!text || pending) return;
    const userMsg: Message = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      ts: Date.now()
    };
    setMessages((m) => [...m, userMsg]);
    setDraft("");
    setPending(true);

    // TODO(backend): POST to /api/claude-chat with SSE streaming.
    // For now, simulate a short latency + canned response so the UX is testable.
    await new Promise((r) => setTimeout(r, 450));
    const stubReply: Message = {
      id: `a-${Date.now()}`,
      role: "assistant",
      content:
        "(Demo mode) The Claude backend isn't wired up in this build yet — when it " +
        "is, I'll stream a real answer here. Until then, your prompt was: " +
        `"${text.slice(0, 140)}${text.length > 140 ? "…" : ""}"`,
      ts: Date.now()
    };
    setMessages((m) => [...m, stubReply]);
    setPending(false);
  };

  const handleReset = () => {
    setMessages([SEED_GREETING]);
    setDraft("");
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 440 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.default"
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "primary.main",
            color: "primary.contrastText"
          }}
        >
          <AutoAwesomeRounded fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              AI Assistant
            </Typography>
            <Chip
              label="BETA"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.625rem", fontWeight: 700, letterSpacing: "0.06em" }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Powered by Claude — ask anything about your bots
          </Typography>
        </Box>
        <Tooltip title="Clear conversation">
          <IconButton onClick={handleReset} aria-label="Clear conversation" size="small">
            <HistoryRounded fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton onClick={onClose} aria-label="Close AI assistant" size="small">
            <CloseRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Message list */}
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2.5,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 1.5
        }}
      >
        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {pending && <TypingIndicator />}
      </Box>

      {/* Quick prompts — shown only when no user message sent yet */}
      {messages.length === 1 && (
        <Box sx={{ px: 2.5, pb: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {QUICK_PROMPTS.map((p) => (
            <Chip
              key={p}
              label={p}
              size="small"
              variant="outlined"
              onClick={() => setDraft(p)}
              sx={{ borderRadius: 2, cursor: "pointer" }}
            />
          ))}
        </Box>
      )}

      {/* Input */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.paper"
        }}
      >
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Submit on plain Enter; Shift+Enter = newline
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void handleSend();
            }
          }}
          placeholder="Ask anything about your bots…"
          fullWidth
          multiline
          maxRows={6}
          disabled={pending}
          InputProps={{
            sx: { borderRadius: 3 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSend}
                  disabled={pending || draft.trim().length === 0}
                  color="primary"
                  aria-label="Send message"
                >
                  <SendRounded />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
          Responses are generated — verify before acting on financial advice.
        </Typography>
      </Box>
    </Drawer>
  );
}

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === "user";
  return (
    <Box
      sx={{
        alignSelf: isUser ? "flex-end" : "flex-start",
        maxWidth: "85%",
        px: 1.75,
        py: 1.25,
        borderRadius: 3,
        bgcolor: isUser ? "primary.main" : "background.paper",
        color: isUser ? "primary.contrastText" : "text.primary",
        borderTopRightRadius: isUser ? 8 : undefined,
        borderTopLeftRadius: !isUser ? 8 : undefined,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        fontSize: "0.9375rem",
        lineHeight: 1.45
      }}
    >
      {message.content}
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        px: 1.75,
        py: 1.25,
        borderRadius: 3,
        bgcolor: "background.paper",
        display: "inline-flex",
        gap: 0.5,
        "& span": {
          width: 6,
          height: 6,
          borderRadius: "50%",
          bgcolor: "text.secondary",
          animation: "whb-typing 1.2s ease-in-out infinite"
        },
        "& span:nth-of-type(2)": { animationDelay: "0.15s" },
        "& span:nth-of-type(3)": { animationDelay: "0.3s" },
        "@keyframes whb-typing": {
          "0%, 80%, 100%": { opacity: 0.3, transform: "translateY(0)" },
          "40%":            { opacity: 1,   transform: "translateY(-3px)" }
        }
      }}
    >
      <span /><span /><span />
    </Box>
  );
}
